import { useEffect, useState } from "react";
import { galleryAPI, uploadAPI } from "../../lib/api";
import { Image, Plus, Trash2, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const CATEGORIES = [
    { value: "show",       label: "Shows" },
    { value: "evento",     label: "Eventos" },
    { value: "mentoria",   label: "Mentoria" },
    { value: "ministerio", label: "Ministério" },
    { value: "bastidores", label: "Bastidores" },
    { value: "outro",      label: "Outros" },
];

export default function GalleryAdmin() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [catFilter, setCatFilter] = useState("");
    const [addModal, setAddModal] = useState(false);
    const [addForm, setAddForm] = useState({ url: "", caption: "", category: "evento", order: 0 });

    const load = async () => {
        setLoading(true);
        try { const r = await galleryAPI.list(); setImages(r.data || []); }
        catch { toast.error("Erro ao carregar galeria"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const uploadImages = async (files) => {
        setUploading(true);
        let ok = 0;
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const r = await uploadAPI.image(fd);
                await galleryAPI.create({ url: r.data.url, caption: "", category: catFilter || "evento", order: images.length + ok });
                ok++;
            } catch { toast.error(`Erro ao enviar ${file.name}`); }
        }
        if (ok > 0) { toast.success(`${ok} imagem(ns) adicionada(s)`); load(); }
        setUploading(false);
    };

    const addByUrl = async () => {
        if (!addForm.url) { toast.error("URL obrigatória"); return; }
        try {
            await galleryAPI.create(addForm);
            toast.success("Imagem adicionada");
            setAddModal(false);
            setAddForm({ url: "", caption: "", category: "evento", order: 0 });
            load();
        } catch { toast.error("Erro ao adicionar"); }
    };

    const del = async (id) => {
        if (!window.confirm("Remover imagem?")) return;
        try { await galleryAPI.delete(id); toast.success("Removida"); load(); }
        catch { toast.error("Erro ao remover"); }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
        if (files.length > 0) uploadImages(files);
    };

    const filtered = images.filter(i => !catFilter || i.category === catFilter);

    return (
        <>
        <Header title="Galeria" subtitle={`${images.length} imagens`} actions={<><button onClick={() => setAddModal(true)} className="border border-stone-700 text-stone-300 hover:text-stone-100 text-sm px-3 py-2 rounded-lg transition-colors">URL externa</button><label className={`flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{uploading ? "Enviando..." : "Upload"}<input type="file" className="hidden" multiple accept="image/*" onChange={e => uploadImages(Array.from(e.target.files))} /></label></>} />
        <div className="p-6 space-y-5">
            {addModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-sm shadow-2xl">
                        <div className="p-5 border-b border-stone-800 flex items-center justify-between">
                            <h2 className="font-bold text-stone-100">Adicionar por URL</h2>
                            <button onClick={() => setAddModal(false)}><X className="w-4 h-4 text-stone-500" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="text-xs text-stone-400 block mb-1">URL da imagem</label>
                                <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                    value={addForm.url} onChange={e => setAddForm(f => ({...f, url: e.target.value}))} placeholder="https://..." />
                            </div>
                            <div>
                                <label className="text-xs text-stone-400 block mb-1">Legenda</label>
                                <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                    value={addForm.caption} onChange={e => setAddForm(f => ({...f, caption: e.target.value}))} />
                            </div>
                            <div>
                                <label className="text-xs text-stone-400 block mb-1">Categoria</label>
                                <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                    value={addForm.category} onChange={e => setAddForm(f => ({...f, category: e.target.value}))}>
                                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                            <button onClick={() => setAddModal(false)} className="px-3 py-2 text-sm text-stone-400">Cancelar</button>
                            <button onClick={addByUrl} className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg transition-colors">Adicionar</button>
                        </div>
                    </div>
                </div>
            )}


            {/* Filtros */}
            <div className="flex gap-1.5 flex-wrap">
                <button onClick={() => setCatFilter("")}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${!catFilter ? "bg-amber-700 border-amber-600 text-white" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                    Todas
                </button>
                {CATEGORIES.map(c => (
                    <button key={c.value} onClick={() => setCatFilter(c.value)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${catFilter === c.value ? "bg-amber-700 border-amber-600 text-white" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                        {c.label}
                    </button>
                ))}
            </div>

            {/* Drop zone + grid */}
            <div onDragOver={e => e.preventDefault()} onDrop={onDrop}
                className="min-h-[200px]">
                {loading ? (
                    <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
                ) : filtered.length === 0 ? (
                    <div className="border-2 border-dashed border-stone-700 rounded-2xl p-12 text-center">
                        <Upload className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                        <p className="text-stone-400 text-sm">Arraste imagens aqui ou use o botão Upload</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {filtered.map(img => (
                            <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden bg-stone-800">
                                <img src={img.url} alt={img.caption || ""} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button onClick={() => del(img.id)}
                                        className="p-2 bg-red-900/80 hover:bg-red-700 rounded-full transition-colors">
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                                {img.caption && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                        <p className="text-xs text-white truncate">{img.caption}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                        {/* Drop target */}
                        <label className="aspect-square rounded-xl border-2 border-dashed border-stone-700 flex items-center justify-center cursor-pointer hover:border-amber-600 transition-colors">
                            <div className="text-center">
                                <Plus className="w-6 h-6 text-stone-600 mx-auto" />
                                <p className="text-xs text-stone-600 mt-1">Adicionar</p>
                            </div>
                            <input type="file" className="hidden" multiple accept="image/*" onChange={e => uploadImages(Array.from(e.target.files))} />
                        </label>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
